import React from 'react';
import { X, Info, DollarSign, Zap, Calendar, Target, TrendingUp} from 'lucide-react';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { getAgreementProgressDetails } from '../../utils/agreementUtils'; 
import '../../styles/ProgressBar.css'; // Importar estilos de la barra de progreso

const AgreementDetailsModal = ({ agreement, userCommissions, onClose, transactions }) => {
    if (!agreement) return null;

    const {
        progress,
        isComplete,
        roiEarnings,
        remainingToGoal,
        goal,
        totalEarnings
    } = getAgreementProgressDetails(agreement, userCommissions || 0, transactions);

    const isAgreementComplete = agreement.status === 'completado' || isComplete;
    const commissionEarnings = userCommissions || 0;
    const activeCapital = agreement.netInvestmentAmount || agreement.amount || 0;

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        
        // Maneja objetos de fecha de Firestore (con toDate), strings de fecha ISO, o números (milisegundos)
        let date;
        if (timestamp.toDate) {
            date = timestamp.toDate();
        } else {
            date = new Date(timestamp);
        }

        // Si después de intentar convertir, la fecha no es válida, devolvemos 'N/A'
        if (isNaN(date.getTime())) return 'N/A';

        return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const DetailItem = ({ icon, label, value, className = '', isHighlighted = false }) => (
        <div className={`detail-item ${className} ${isHighlighted ? 'highlighted' : ''}`}>
            <div className="detail-item-label">
                {icon}
                <span>{label}</span>
            </div>
            <div className="detail-item-value">{value}</div>
        </div>
    );

    const CompletedSummary = () => (
        <div className="details-card completed-summary-card">
            <div className="completed-header">
                <CheckCircle size={24} className="completed-icon" />
                <h4 className="card-title">Acuerdo Completado</h4>
            </div>
            <p className="completed-subtitle">Este acuerdo ha alcanzado su meta del 200% y ha finalizado.</p>
            
            <div className="completed-financials">
                <DetailItem icon={<DollarSign size={16} />} label="Participación neta" value={`$${activeCapital.toFixed(2)}`} />
                <DetailItem icon={<TrendingUp size={16} />} label="Resultado acumulado" value={`$${totalEarnings.toFixed(2)}`} />
                <DetailItem icon={<Target size={16} />} label="Retorno Total (200%)" value={`$${(activeCapital + totalEarnings).toFixed(2)}`} isHighlighted={true} />
            </div>

            <div className="completed-dates">
                <DetailItem icon={<Calendar size={16} />} label="Fecha de Inicio" value={formatDate(agreement.approvedAt)} />
                <DetailItem icon={<Calendar size={16} />} label="Fecha de Finalización" value={formatDate(agreement.completedAt)} />
            </div>
        </div>
    );

    const FinalStatusSummary = ({ status }) => {
        const isCancelled = status === 'cancelado';
        const title = isCancelled ? 'Acuerdo Cancelado' : 'Acuerdo Rechazado';
        const reason = isCancelled ? agreement.cancellationReason : agreement.rejectionReason;
        const dateLabel = isCancelled ? 'Fecha de Cancelación' : 'Fecha de Revisión';
        const dateValue = formatDate(isCancelled ? agreement.cancelledAt : agreement.reviewedAt || agreement.cancelledAt);

        return (
            <div className="details-card final-status-summary-card">
                <div className="final-status-header">
                    <AlertTriangle size={24} className="final-status-icon" />
                    <h4 className="card-title">{title}</h4>
                </div>
                <div className="reason-section">
                    <h5>Motivo:</h5>
                    <p>{reason || 'No se especificó un motivo.'}</p>
                </div>
                <div className="final-status-dates">
                    <DetailItem icon={<Calendar size={16} />} label={dateLabel} value={dateValue} />
                </div>
            </div>
        );
    };

    const ActiveProgress = () => (
        <div className="details-card progress-card">
            <h4 className="card-title">Progreso hacia la Duplicación</h4>
            
            <div className="progress-percentage-header">
                <span className="progress-display-text card-value-dark">{progress.toFixed(2)}% de la meta</span>
            </div>

            <div className="progress-bar-superposed-wrapper">
                <div className="progress-bar-background" />
                <div className={`progress-bar-fill ${progress > 50 ? 'profit-zone' : ''} ${isComplete ? 'complete' : ''}`} style={{ width: `${progress}%` }} />
            </div>

            <div className="progress-breakdown">
                <div className="breakdown-section">
                    <h5 className="breakdown-title">Detalle de resultados</h5>
                    <DetailItem icon={<TrendingUp size={16} />} label="Resultados del acuerdo" value={`$${roiEarnings.toFixed(2)}`} />
                    <DetailItem icon={<Zap size={16} />} label="Resultados por comisiones" value={`$${commissionEarnings.toFixed(2)}`} />
                    <DetailItem icon={<DollarSign size={16} />} label="Resultado acumulado" value={`$${totalEarnings.toFixed(2)}`} isHighlighted={true} />
                </div>
                <div className="breakdown-section">
                    <h5 className="breakdown-title">Meta de resultados</h5>
                    <DetailItem icon={<Target size={16} />} label="Meta (200%)" value={`$${goal.toFixed(2)}`} />
                    <DetailItem icon={<Info size={16} />} label="Resultados restantes" value={`$${remainingToGoal.toFixed(2)}`} />
                </div>
            </div>
        </div>
    );

    const renderContent = () => {
        if (isAgreementComplete) {
            return <CompletedSummary />;
        }
        if (agreement.status === 'cancelado' || agreement.status === 'rechazado') {
            return <FinalStatusSummary status={agreement.status} />;
        }
        return <ActiveProgress />;
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content details-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Detalles del Acuerdo</h3>
                    <button onClick={onClose} className="close-button"><X size={24} /></button>
                </div>
              {/*   <div className="modal-body">
                     //<div className="details-card">
                    //    <h4 className="card-title">Información General</h4>
                    //    <div className="general-summary">
                   //         <DetailItem icon={<Hash size={16} />} label="Acuerdo ID" value={agreement.id} />
                    //        <DetailItem icon={<Calendar size={16} />} label="Fecha de Creación" value={formatDate(agreement.createdAt)} />
                    //        <DetailItem icon={<Info size={16} />} label="Estado" value={<StatusBadge status={isAgreementComplete ? 'completado' : agreement.status} />} />
                     //   </div>
                    </div>*/}

                    {/* El resumen de inversión solo se muestra para acuerdos activos o pendientes */}
                    {['aprobado', 'pendiente'].includes(agreement.status) && (
                        <div className="details-card">
                            <h4 className="card-title">Resumen del acuerdo</h4>
                            <div className="financial-summary">
                                <DetailItem icon={<DollarSign size={16} />} label="Participación bruta" value={`$${(agreement.amount || 0).toFixed(2)}`} />
                                {agreement.acceleratorFee > 0 && (
                                    <DetailItem icon={<Zap size={16} />} label="Comisión Acelerador" value={`-$${(agreement.acceleratorFee || 0).toFixed(2)}`} className="fee-item" />
                                )}
                                <DetailItem icon={<DollarSign size={16} />} label="Participación neta (Monto activo)" value={`$${activeCapital.toFixed(2)}`} className="net-item" />
                            </div>
                        </div>
                    )}

                    {renderContent()}
                </div>
            </div>
        
    );
};

export default AgreementDetailsModal;
